import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usePortal } from '../../context/PortalContext';
import { useSupportContext } from '../../context/SupportContext';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Check } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { IconInput } from '../../components/ui/icon-input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { useTheme } from '../../context/ThemeContext';
import FundEliteBrandMark from '../../components/branding/FundEliteBrandMark';
import {
  getLoginErrorMessage,
  resolveLoginReturnPath,
  getGoogleOAuthStartUrl,
  getGoogleAuthErrorMessage,
  agencyOwnerNeedsEmailVerification,
  getDashboardForUser,
  applyPostAuthRedirect,
  AGENCY_DASHBOARD_PATH,
  clearStaleAuthRedirectStorage,
  sanitizeAuthRedirectUrl,
  FUNDGENIUS_HQ_CLIENTS_PATH,
} from '../../utils/authUtils';
import {
  DEV_AUTH_EMAIL,
  DEV_AUTH_PASSWORD,
  DEV_FUNDGENIUS_CLIENTS_PATH,
  isDevAuthEnabled,
  isDevAuthToken,
  logDevAuthWarning,
} from '../../utils/devAuth';
import { AUTH_TOKEN_KEY } from '../../utils/authUtils';
import {
  getFundGeniusLoginBranding,
  getFundGeniusPostLoginPath,
  isFundGeniusAppLoginHost,
} from '../../utils/fundgeniusLoginBranding';
import {
  getPortalLandingRoute,
  isFundElitePlatformLandingHost,
  isFundGeniusPath,
} from '../../utils/portalLandingRoute';

export const resolvePostLoginDestination = ({
  from,
  user,
  isInSupportMode,
  supportContext,
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
}) => {
  if (agencyOwnerNeedsEmailVerification(user)) {
    return '/verify-email';
  }

  const safeFrom = sanitizeAuthRedirectUrl(from, { hostname }) || (
    from && String(from).startsWith('/') && !String(from).includes('fundelite')
      ? from
      : null
  );

  // FundElite platform host: never land on FundGenius from root / empty return path.
  // Platform Owner / Admin default is always /platform/dashboard (not Agencies).
  if (isFundElitePlatformLandingHost(hostname)) {
    if (
      !safeFrom
      || safeFrom === '/'
      || safeFrom === '/login'
      || safeFrom === '/signin'
      || isFundGeniusPath(safeFrom)
    ) {
      return getPortalLandingRoute({ hostname, user });
    }
  }

  const fundGeniusPath = getFundGeniusPostLoginPath({
    user,
    supportContext,
    isInSupportMode,
    hostname,
  });

  // On FundGenius hosts, never honor stale FundElite return paths.
  if (fundGeniusPath) {
    if (
      safeFrom
      && (safeFrom.startsWith('/platform/fundgenius-hq') || safeFrom.startsWith('/fundgenius-hq'))
    ) {
      return safeFrom;
    }
    if (
      safeFrom
      && isInSupportMode
      && (safeFrom.startsWith('/platform') || safeFrom.startsWith('/staff') || safeFrom.startsWith('/admin'))
    ) {
      const portal = supportContext?.effective_portal_type;
      if (portal === 'managed_client_portal') return '/client/dashboard';
      if (portal === 'fundgenius_hq' || portal === 'fundgenius') {
        const agencyId = supportContext?.effective_agency_id;
        return agencyId
          ? `/platform/fundgenius-hq/clients?agency_id=${encodeURIComponent(agencyId)}`
          : FUNDGENIUS_HQ_CLIENTS_PATH;
      }
      if (portal === 'staff_portal') return '/staff/dashboard';
      return fundGeniusPath;
    }
    return fundGeniusPath;
  }

  if (safeFrom && safeFrom !== '/login' && safeFrom !== '/signin' && safeFrom !== '/') {
    if (
      isInSupportMode &&
      (safeFrom.startsWith('/platform') || safeFrom.startsWith('/staff') || safeFrom.startsWith('/admin'))
    ) {
      const portal = supportContext?.effective_portal_type;
      if (portal === 'managed_client_portal') return '/client/dashboard';
      if (portal === 'fundgenius_hq' || portal === 'fundgenius') {
        const agencyId = supportContext?.effective_agency_id;
        return agencyId
          ? `/platform/fundgenius-hq/clients?agency_id=${encodeURIComponent(agencyId)}`
          : FUNDGENIUS_HQ_CLIENTS_PATH;
      }
      if (portal === 'staff_portal') return '/staff/dashboard';
      return '/dashboard';
    }
    return safeFrom;
  }

  // Final default: shared hostname/role resolver (never Agencies as implicit home).
  return getPortalLandingRoute({ hostname, user });
};

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user, loading: authLoading, awaitingAccountSelection } = useAuth();
  const { refreshPortalData } = usePortal();
  const { isInSupportMode, supportContext, contextReady } = useSupportContext();
  const { resolvedMode } = useTheme();
  const brandTheme = resolvedMode === 'light' ? 'light' : 'dark';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const hasRedirectedRef = useRef(false);
  const branding = getFundGeniusLoginBranding(
    typeof window !== 'undefined' ? window.location.hostname : ''
  );
  const isFundGeniusLogin = Boolean(branding);

  useEffect(() => {
    if (isFundGeniusLogin || isFundGeniusAppLoginHost()) {
      clearStaleAuthRedirectStorage();
    }
  }, [isFundGeniusLogin]);

  // Get the intended destination from state or URL params (keep ?package_id= etc.)
  const from =
    resolveLoginReturnPath(location.state?.from, window.location.origin) ||
    resolveLoginReturnPath(
      new URLSearchParams(location.search).get('redirect'),
      window.location.origin
    );

  // If user is already logged in, redirect them once (avoid redirect loops)
  useEffect(() => {
    if (authLoading || !user || hasRedirectedRef.current) return;

    if (
      awaitingAccountSelection
      || user.requires_account_selection
      || user.pending_account_selection
    ) {
      hasRedirectedRef.current = true;
      navigate('/choose-account', { replace: true });
      return;
    }

    const platformConsoleRole =
      user.role === 'platform_owner' || user.role === 'super_admin' || user.role === 'platform_admin';
    if (platformConsoleRole && !contextReady) return;

    const destination = resolvePostLoginDestination({
      from,
      user,
      isInSupportMode,
      supportContext,
    });

    hasRedirectedRef.current = true;
    applyPostAuthRedirect(user, navigate, destination);
  }, [
    authLoading,
    user,
    from,
    navigate,
    isInSupportMode,
    supportContext,
    contextReady,
    awaitingAccountSelection,
  ]);

  const isGoogleLoginEnabled =
    process.env.REACT_APP_GOOGLE_LOGIN_ENABLED !== 'false' &&
    (process.env.REACT_APP_GOOGLE_CLIENT_ID || process.env.REACT_APP_BACKEND_URL);

  useEffect(() => {
    logDevAuthWarning();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const oauthError = params.get('error');
    if (oauthError?.startsWith('google_auth')) {
      toast.error(getGoogleAuthErrorMessage(oauthError, params.get('detail')));
    }
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const userData = await login(email, password);
      const usedDevBypass = isDevAuthToken(localStorage.getItem(AUTH_TOKEN_KEY));
      toast.success(
        usedDevBypass
          ? 'Local dev login — opening Funding workspace'
          : isFundGeniusLogin
            ? 'Welcome to FundGenius HQ'
            : 'Welcome back!'
      );

      if (usedDevBypass) {
        navigate(`${DEV_FUNDGENIUS_CLIENTS_PATH}?agency_id=agency_internal_001`, { replace: true });
        return;
      }

      if (userData?.requires_account_selection || userData?.account_selection_required) {
        // Identity-only: do not applyPostAuthRedirect / resolvePostAuthTarget / portal shell.
        navigate('/choose-account', {
          replace: true,
          state: {
            accounts: userData.accounts || userData.selection_challenge?.accounts || [],
            selection_token: userData.selection_token || userData.challenge_id,
            challenge_id: userData.challenge_id || userData.selection_token,
            user: {
              email: userData.email,
              display_name: userData.display_name || userData.name,
              user_id: userData.user_id,
            },
          },
        });
        return;
      }

      if (userData?.redirect_to && String(userData.redirect_to).startsWith('/')) {
        navigate(userData.redirect_to, { replace: true });
        return;
      }

      if (agencyOwnerNeedsEmailVerification(userData)) {
        navigate('/verify-email', {
          replace: true,
          state: {
            email: userData.email,
            role: 'agency_owner',
            redirectTo: isFundGeniusLogin
              ? (getFundGeniusPostLoginPath({ user: userData }) || FUNDGENIUS_HQ_CLIENTS_PATH)
              : AGENCY_DASHBOARD_PATH,
            agencyId: userData.agency_id,
          },
        });
        return;
      }

      // IMPORTANT: Check for intended destination FIRST, before any other redirects
      // This ensures that if user was trying to access /dispute-wizard/metro2,
      // they get redirected there after login instead of dashboard
      if (from && from !== '/login' && from !== '/signin' && from !== '/') {
        const destination = resolvePostLoginDestination({
          from,
          user: userData,
          isInSupportMode,
          supportContext,
        });
        applyPostAuthRedirect(userData, navigate, destination);
        return;
      }

      const fundGeniusDestination = getFundGeniusPostLoginPath({
        user: userData,
        supportContext,
        isInSupportMode,
      });
      if (fundGeniusDestination) {
        applyPostAuthRedirect(userData, navigate, fundGeniusDestination);
        return;
      }

      // Refresh portal data to get correct redirect
      await refreshPortalData();

      applyPostAuthRedirect(userData, navigate);
    } catch (error) {
      toast.error(getLoginErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const startUrl = getGoogleOAuthStartUrl(from);
    window.location.href = startUrl;
  };

  if (isFundGeniusLogin) {
    return (
      <div
        className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950 flex"
        data-testid={branding.testId}
      >
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-emerald-700 via-emerald-800 to-slate-950 p-12 flex-col justify-between relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-16 left-16 w-72 h-72 bg-emerald-300 rounded-full blur-3xl" />
            <div className="absolute bottom-16 right-16 w-96 h-96 bg-slate-200 rounded-full blur-3xl" />
          </div>

          <div className="relative">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center text-white font-bold tracking-wide">
                {branding.brandMark}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">{branding.brandName}</h1>
                <p className="text-sm text-emerald-100/80">{branding.positioning}</p>
              </div>
            </div>
          </div>

          <div className="relative">
            <h2 className="text-4xl font-bold text-white leading-tight mb-6">
              {branding.headline}
            </h2>
            <p className="text-lg text-emerald-50/85 max-w-md mb-10">
              {branding.subheadline}
            </p>
            <ul className="space-y-3">
              {branding.valuePoints.map((point) => (
                <li key={point} className="flex items-center gap-3 text-white/90">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-400/20 text-emerald-200">
                    <Check size={14} />
                  </span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="relative text-white/55 text-sm">
            {branding.footerCopyright}
          </div>
        </div>

        <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-50">
          <div className="w-full max-w-md">
            <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
              <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center text-white text-sm font-bold">
                {branding.brandMark}
              </div>
              <span className="text-xl font-bold text-stone-900">{branding.brandName}</span>
            </div>

            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-stone-900" data-testid="login-headline">
                {branding.formTitle}
              </h2>
              <p className="text-slate-500 mt-2" data-testid="login-subheadline">
                {branding.formSubtitle}
              </p>
            </div>

            {isDevAuthEnabled() ? (
              <div
                className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-left text-sm text-amber-950"
                data-testid="dev-auth-hint"
              >
                <p className="font-semibold">Local development login</p>
                <p className="mt-1">
                  Email: <code className="rounded bg-white px-1.5 py-0.5">{DEV_AUTH_EMAIL}</code>
                </p>
                <p>
                  Password: <code className="rounded bg-white px-1.5 py-0.5">{DEV_AUTH_PASSWORD}</code>
                </p>
                <p className="mt-2 text-xs text-amber-800">Local dev auth enabled. Do not use in production.</p>
                <button
                  type="button"
                  className="mt-3 text-xs font-semibold text-emerald-700 hover:underline"
                  onClick={() => {
                    setEmail(DEV_AUTH_EMAIL);
                    setPassword(DEV_AUTH_PASSWORD);
                  }}
                >
                  Fill dev credentials
                </button>
              </div>
            ) : null}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="email" className="text-slate-700">Email Address</Label>
                <IconInput
                  id="email"
                  type="email"
                  icon={Mail}
                  containerClassName="mt-1.5"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email"
                />
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-slate-700">Password</Label>
                  <Link to="/forgot-password" className="text-sm text-emerald-600 hover:text-emerald-700">
                    Forgot password?
                  </Link>
                </div>
                <IconInput
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  icon={Lock}
                  containerClassName="mt-1.5"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                  trailing={(
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 z-[1] flex w-11 items-center justify-center text-slate-400 hover:text-slate-600"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  )}
                />
              </div>

              <Button
                type="submit"
                className="w-full btn-primary py-3"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? 'Signing in...' : branding.ctaLabel}
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </form>

            {isGoogleLoginEnabled && (
              <>
                <div className="relative my-8">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-slate-50 text-slate-500">Or continue with</span>
                  </div>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full py-3 border-slate-200 hover:bg-slate-50"
                  onClick={handleGoogleLogin}
                  data-testid="google-login-btn"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </Button>
              </>
            )}

            <p className="text-center mt-8 text-slate-500">
              Don't have an account?{' '}
              <Link
                to={branding.signupPath}
                className="text-emerald-600 hover:text-emerald-700 font-medium"
                data-testid="login-signup-link"
              >
                {branding.signupLabel}
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex" data-testid="login-page">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-emerald-500 via-emerald-600 to-emerald-800 p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>

        <div className="relative">
          <div className="flex items-center gap-3 mb-8" data-testid="fundelite-login-brand">
            <FundEliteBrandMark
              variant="self-repair"
              mode="logo"
              theme={brandTheme}
              logoClassName="h-10 w-auto max-w-[200px] object-contain object-left"
            />
          </div>
        </div>

        <div className="relative">
          <h2 className="text-4xl font-bold text-white leading-tight mb-6">
            Transform Your <br />Credit Journey
          </h2>
          <p className="text-lg text-white/80 max-w-md">
            AI-powered credit analysis, strategic dispute workflows, and funding readiness optimization — all in one platform.
          </p>

          <div className="mt-12 grid grid-cols-2 gap-4">
            <div className="glass-card p-4">
              <p className="text-3xl font-bold text-white">85%</p>
              <p className="text-sm text-white/70">Dispute Success Rate</p>
            </div>
            <div className="glass-card p-4">
              <p className="text-3xl font-bold text-white">+67</p>
              <p className="text-sm text-white/70">Avg Point Increase</p>
            </div>
          </div>
        </div>

        <div className="relative text-white/60 text-sm">
          © 2026 FundElite AI. All rights reserved.
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center mb-8" data-testid="fundelite-login-brand-mobile">
            <FundEliteBrandMark
              variant="self-repair"
              mode="logo"
              theme={brandTheme}
              logoClassName="h-9 w-auto max-w-[180px] object-contain"
            />
          </div>

          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-stone-900" data-testid="login-headline">Welcome back</h2>
            <p className="text-slate-500 mt-2" data-testid="login-subheadline">Sign in to continue to your dashboard</p>
          </div>

          {isDevAuthEnabled() ? (
            <div
              className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-left text-sm text-amber-950"
              data-testid="dev-auth-hint"
            >
              <p className="font-semibold">Local development login</p>
              <p className="mt-1">
                Email: <code className="rounded bg-white px-1.5 py-0.5">{DEV_AUTH_EMAIL}</code>
              </p>
              <p>
                Password: <code className="rounded bg-white px-1.5 py-0.5">{DEV_AUTH_PASSWORD}</code>
              </p>
              <p className="mt-2 text-xs text-amber-800">Local dev auth enabled. Do not use in production.</p>
              <button
                type="button"
                className="mt-3 text-xs font-semibold text-emerald-700 hover:underline"
                onClick={() => {
                  setEmail(DEV_AUTH_EMAIL);
                  setPassword(DEV_AUTH_PASSWORD);
                }}
              >
                Fill dev credentials
              </button>
            </div>
          ) : null}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-slate-700">Email Address</Label>
              <IconInput
                id="email"
                type="email"
                icon={Mail}
                containerClassName="mt-1.5"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-slate-700">Password</Label>
                <Link to="/forgot-password" className="text-sm text-emerald-600 hover:text-emerald-700">
                  Forgot password?
                </Link>
              </div>
              <IconInput
                id="password"
                type={showPassword ? 'text' : 'password'}
                icon={Lock}
                containerClassName="mt-1.5"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                trailing={(
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 z-[1] flex w-11 items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-[#EAF7F0]"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                )}
              />
            </div>

            <Button
              type="submit"
              className="w-full btn-primary py-3"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? 'Signing in...' : 'Sign In'}
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </form>

          {isGoogleLoginEnabled && (
            <>
              <div className="relative my-8">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-slate-50 text-slate-500">Or continue with</span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full py-3 border-slate-200 hover:bg-slate-50"
                onClick={handleGoogleLogin}
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>
            </>
          )}

          <p className="text-center mt-8 text-slate-500">
            Don't have an account?{' '}
            <Link to="/signup" className="text-emerald-600 hover:text-emerald-700 font-medium">
              Sign up for free
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
